import { useMemo } from 'react';
import GraphQlImageModel from '@/components/interfaces/GraphQlImageModel';
import { Image, MergeImagesResponse } from './types';
import useDuplicateImagesStore from './useDuplicateImagesStore';

// Sort checked items to the left first, then sort by numberOfUses
export const useSortedDuplicateImages = (duplicateImages: Image[]) => {
  const checkedImageIds = useDuplicateImagesStore(state => state.checkedImageIds);

  return useMemo(() => (
    [...duplicateImages].sort((i1, i2) => {
      const i1Checked = checkedImageIds.has(i1.id);
      const i2Checked = checkedImageIds.has(i2.id);

      if (i1Checked && !i2Checked) return -1;
      if (!i1Checked && i2Checked) return 1;

      return i2.numberOfUses - i1.numberOfUses;
    })
  ), [checkedImageIds, duplicateImages]);
};

export const convertToGraphQlImageModels = (response: MergeImagesResponse): GraphQlImageModel[] => {
  const { image } = response;

  return response.updated_image_models.map(imageModel => ({
    captionEn: imageModel.caption_en,
    captionEs: imageModel.caption_es,
    decorative: imageModel.decorative,
    displaySize: imageModel.display_size as GraphQlImageModel['displaySize'],
    hideCaptions: imageModel.hide_captions,
    id: imageModel.id.toString(),
    image: {
      descriptionEn: image.description_en,
      englishImageOnly: image.english_image_only,
      hasText: image.has_text,
      imageEsUrl: image.imageEsUrl || image.es_url,
      imageUrl: image.imageUrl || image.url,
    },
    image_credits: image.credits,
    image_descriptionConfidence: image.description_confidence,
    image_descriptionEn: image.description_en,
    image_descriptionEs: image.description_es,
    image_fileName: image.fileName || image.image_en_file_name,
    image_fileNameEs: image.fileNameEs || image.image_es_file_name,
    image_titleEn: image.title_en,
    image_titleEs: image.title_es,
    mediaMeta: imageModel.media_meta,
    modelId: imageModel.model_id,
    modelType: imageModel.model_type,
    snippet: `[IMAGE_MODEL_${imageModel.id}]`,
    zoomType: imageModel.zoom_type as GraphQlImageModel['zoomType'],
  }));
};

